import {
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import {
  getMobilePdfPageScrollTop,
  pickMostVisiblePage
} from '@pages/MediaViewer/AssetViewer/PdfCard/utils';
import {
  IAssetViewerStateContextValue,
  ShapesDrawingToolsProps
} from '@context/AssetViewerStateContext';

type UseMobilePdfScrollParams = {
  wrapperRef: RefObject<OverlayScrollbarsComponentRef<'div'>>;
  pageRefsMap: MutableRefObject<Map<number, HTMLDivElement>>;
  skipActivePageScrollSyncRef: MutableRefObject<boolean>;
  pagesArray: { page: number }[];
  pagesCount: number;
  rowPitch: number;
  isPageSizeReady: boolean;
  pageRefsVersion: number;
  activePageNumber: number;
  selectedCommentId: string | null;
  shapesDrawingTools: ShapesDrawingToolsProps;
  runMobileCanvasLayout: () => void;
  setActivePageNumber: IAssetViewerStateContextValue['setActivePageNumber'];
  selectComment: IAssetViewerStateContextValue['selectComment'];
  setDocumentHighlight: IAssetViewerStateContextValue['setDocumentHighlight'];
  setCanvasMode: IAssetViewerStateContextValue['setCanvasMode'];
};

export function useMobilePdfScroll({
  wrapperRef,
  pageRefsMap,
  skipActivePageScrollSyncRef,
  pagesArray,
  pagesCount,
  rowPitch,
  isPageSizeReady,
  pageRefsVersion,
  activePageNumber,
  selectedCommentId,
  shapesDrawingTools,
  runMobileCanvasLayout,
  setActivePageNumber,
  selectComment,
  setDocumentHighlight,
  setCanvasMode
}: UseMobilePdfScrollParams) {
  const activePageFromScrollRef = useRef(false);
  const lastScrolledPageRef = useRef(activePageNumber);
  const pendingScrollToPageRef = useRef<number | null>(null);

  const [renderRange, setRenderRange] = useState({ start: 1, end: 12 });
  const [visiblePageNumber, setVisiblePageNumber] = useState(activePageNumber);

  useEffect(() => {
    setVisiblePageNumber(activePageNumber);
  }, [activePageNumber]);

  const scrollMobilePdfToPage = useCallback(
    (pageNumber: number) => {
      const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
      const pageEl = pageRefsMap.current.get(pageNumber);
      if (!viewport || !pageEl) {
        pendingScrollToPageRef.current = pageNumber;
        return false;
      }

      pendingScrollToPageRef.current = null;
      skipActivePageScrollSyncRef.current = true;
      viewport.scrollTo({
        top: getMobilePdfPageScrollTop(viewport, pageEl),
        behavior: 'instant'
      });
      window.setTimeout(() => {
        skipActivePageScrollSyncRef.current = false;
      }, 500);
      return true;
    },
    [wrapperRef, pageRefsMap, skipActivePageScrollSyncRef]
  );

  const updateActivePageFromScroll = useCallback(() => {
    if (skipActivePageScrollSyncRef.current) return;

    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    if (!viewport) return;

    const nextPage = pickMostVisiblePage(
      pagesArray,
      pageRefsMap.current,
      viewport
    );
    if (!nextPage) return;

    setVisiblePageNumber(nextPage);

    if (selectedCommentId) return;
    if (nextPage === activePageNumber) return;

    activePageFromScrollRef.current = true;
    lastScrolledPageRef.current = nextPage;
    setActivePageNumber(nextPage);
    if (shapesDrawingTools.shape !== 'documentHighlight') {
      selectComment(null);
      setDocumentHighlight(null);
      setCanvasMode('view');
    }
  }, [
    wrapperRef,
    pageRefsMap,
    skipActivePageScrollSyncRef,
    pagesArray,
    activePageNumber,
    shapesDrawingTools.shape,
    selectedCommentId,
    setActivePageNumber,
    selectComment,
    setDocumentHighlight,
    setCanvasMode
  ]);

  const updateRenderWindow = useCallback(() => {
    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    if (!viewport || !rowPitch) return;

    const padTop = 8;
    const buffer = 8;
    const { scrollTop, clientHeight } = viewport;
    const firstVisible = Math.floor((scrollTop - padTop) / rowPitch) + 1;
    const lastVisible =
      Math.floor((scrollTop - padTop + clientHeight) / rowPitch) + 1;
    const start = Math.max(1, firstVisible - buffer);
    const end = Math.min(pagesCount, lastVisible + buffer);

    setRenderRange((prev) =>
      prev.start === start && prev.end === end ? prev : { start, end }
    );
  }, [wrapperRef, rowPitch, pagesCount]);

  useEffect(() => {
    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    if (!viewport) return undefined;

    const frameId = requestAnimationFrame(updateActivePageFromScroll);

    viewport.addEventListener('scroll', updateActivePageFromScroll, {
      passive: true
    });
    window.addEventListener('resize', updateActivePageFromScroll);

    return () => {
      cancelAnimationFrame(frameId);
      viewport.removeEventListener('scroll', updateActivePageFromScroll);
      window.removeEventListener('resize', updateActivePageFromScroll);
    };
  }, [wrapperRef, updateActivePageFromScroll, pageRefsVersion]);

  useEffect(() => {
    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    if (!viewport) return undefined;

    updateRenderWindow();

    viewport.addEventListener('scroll', updateRenderWindow, { passive: true });
    window.addEventListener('resize', updateRenderWindow);

    return () => {
      viewport.removeEventListener('scroll', updateRenderWindow);
      window.removeEventListener('resize', updateRenderWindow);
    };
  }, [wrapperRef, updateRenderWindow, isPageSizeReady]);

  useEffect(() => {
    if (activePageFromScrollRef.current) {
      activePageFromScrollRef.current = false;
      lastScrolledPageRef.current = activePageNumber;
      return;
    }

    if (activePageNumber === lastScrolledPageRef.current) return;
    lastScrolledPageRef.current = activePageNumber;

    if (scrollMobilePdfToPage(activePageNumber)) {
      requestAnimationFrame(() => {
        runMobileCanvasLayout();
      });
    }
  }, [activePageNumber, scrollMobilePdfToPage, runMobileCanvasLayout]);

  useEffect(() => {
    if (pendingScrollToPageRef.current == null) return;
    if (scrollMobilePdfToPage(pendingScrollToPageRef.current)) {
      requestAnimationFrame(() => {
        runMobileCanvasLayout();
      });
    }
  }, [pageRefsVersion, scrollMobilePdfToPage, runMobileCanvasLayout]);

  return {
    renderRange,
    visiblePageNumber,
    updateActivePageFromScroll
  };
}
